import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { AutoDeclineExplainerService } from '../../../../../../services/auto-decline-explainer/auto-decline-explainer.service';
import { countdownView } from '../../assignment-view.helpers';
import { toEpochMs } from '../application-expiry-bar/application-expiry-bar.helpers';
import { formatStageElapsed } from '../application-stage-progress/application-stage-progress.helpers';
import { MbaIcon } from '../../atoms/mba-icon/mba-icon';
import { ui } from '../../assignment-ui';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Time left to answer an application before it auto-declines. Only rendered when the
 * brand has auto-decline on; clicking it explains the rule, as the old stage bar did.
 */
@Component({
  selector: 'mbt-application-countdown',
  imports: [MbaIcon],
  template: `
    @if (view(); as v) {
      <button type="button" data-row-click-ignore [style.--cd]="v.colour" [title]="tooltip()" (click)="explain($event)"
        [attr.aria-label]="v.label + ', ' + v.timeLeft"
        class="flex cursor-pointer flex-col gap-1.5 rounded-lg text-left focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-(--app-accent-ring)"
        [class]="size() === 'panel' ? 'max-w-none rounded-xl border border-(--border-subtle) px-3.5 py-3' : 'min-w-[190px] max-w-[260px]'">
        <span class="overflow-hidden bg-(--bg-alt) shadow-[inset_0_0_0_1px_var(--border-subtle)]"
          [class]="size() === 'panel' ? 'h-2.5 rounded-[5px]' : 'h-2 rounded'">
          <i class="block h-full rounded bg-(--cd) transition-[width,background-color] duration-[360ms] ease-[cubic-bezier(0.2,0,0,1)]"
            [style.width.%]="v.widthPercent"></i>
        </span>
        <span class="flex flex-wrap items-center gap-1.5">
          <mba-icon name="clock" size="size-[15px] text-(--cd)" />
          <b class="whitespace-nowrap font-(family-name:--font-display) font-extrabold text-(--cd)"
            [class]="size() === 'panel' ? 'text-[19px]' : 'text-base'">{{ v.label }}</b>
          <span class="whitespace-nowrap {{ ui.sub }}">{{ v.timeLeft }}@if (appliedAgo()) { · applied {{ appliedAgo() }}}</span>
        </span>
      </button>
    }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationCountdown {
  protected readonly ui = ui;

  private readonly explainer = inject(AutoDeclineExplainerService);

  readonly autoDeclineAt = input<string | Date | null | undefined>(null);
  readonly appliedAt = input<string | Date | null | undefined>(null);
  readonly autoDeclineDays = input<number | null | undefined>(null);
  readonly nowMs = input.required<number>();
  readonly size = input<'row' | 'panel'>('row');

  private readonly windowMs = computed(() => {
    const days = this.autoDeclineDays();
    if (typeof days === 'number' && days > 0) {
      return days * DAY_MS;
    }
    const end = toEpochMs(this.autoDeclineAt());
    const start = toEpochMs(this.appliedAt());
    return end != null && start != null ? end - start : null;
  });

  protected readonly view = computed(() => {
    const end = toEpochMs(this.autoDeclineAt());
    const windowMs = this.windowMs();
    if (end == null || windowMs == null || windowMs <= 0) {
      return null;
    }
    return countdownView(end - this.nowMs(), windowMs);
  });

  protected readonly appliedAgo = computed(() => {
    const start = toEpochMs(this.appliedAt());
    return start == null ? '' : formatStageElapsed(this.nowMs() - start);
  });

  protected readonly tooltip = computed(() => {
    const windowMs = this.windowMs();
    const days = windowMs ? Math.round(windowMs / DAY_MS) : null;
    return days ? `Applications close ${days} ${days === 1 ? 'day' : 'days'} after applying` : 'Applications close automatically';
  });

  protected explain(event: Event): void {
    event.stopPropagation();
    this.explainer.open(this.autoDeclineDays());
  }
}
